import { DialogueContentPayload } from '@common/api';
import { reactive } from 'vue';

import { ClientWorldService } from './ClientWorldService';
import { InputBlockingService } from './InputBlockingService';

/**
 * Reactive state for the dialogue UI.
 */
export interface DialogueState {
  /** Whether a dialogue is currently active */
  active: boolean;
  /** Current dialogue text */
  text: string;
  /** Speaker name (from ink tags) */
  speaker?: string;
  /** Available choices (empty if none) */
  choices: { index: number; text: string }[];
  /** Whether we can click to continue */
  canContinue: boolean;
  /** The entity we're talking to */
  targetEntityId: number | null;
  /** Spritesheet ID of the target entity for portrait rendering */
  spritesheetId: string | null;
}

/**
 * Client-side service for managing dialogue UI state.
 * Handles reactive state updates from server and triggers network messages for player actions.
 */
export class ClientDialogueService {
  public state: DialogueState;
  private readonly clientWorld: ClientWorldService;
  private sendChoice: ((choiceIndex: number) => void) | null = null;
  private sendContinue: (() => void) | null = null;
  private sendExit: (() => void) | null = null;
  private inputBlocking: InputBlockingService | null = null;

  constructor(clientWorld: ClientWorldService) {
    this.clientWorld = clientWorld;
    this.state = reactive<DialogueState>({
      active: false,
      text: '',
      speaker: undefined,
      choices: [],
      canContinue: false,
      targetEntityId: null,
      spritesheetId: null,
    });
  }

  /**
   * Set the callbacks for sending messages to the server.
   */
  setCallbacks(
    sendChoice: (choiceIndex: number) => void,
    sendContinue: () => void,
    sendExit: () => void,
  ): void {
    this.sendChoice = sendChoice;
    this.sendContinue = sendContinue;
    this.sendExit = sendExit;
  }

  setInputBlocking(service: InputBlockingService): void {
    this.inputBlocking = service;
  }

  /**
   * Resolve the spritesheet ID for a given entity from the client world.
   */
  public resolveSpritesheetId(serverEntityId: number): string | null {
    const clientEntityId = this.clientWorld.getClientEntityId(serverEntityId);
    if (clientEntityId === undefined) return null;

    const sprite = this.clientWorld.getWorld().getComponent(clientEntityId, 'SpritesheetSprite');
    return sprite?.spritesheetId ?? null;
  }

  /**
   * Resolve the display name for a given entity from the client world.
   */
  private resolveDisplayName(serverEntityId: number): string | null {
    const clientEntityId = this.clientWorld.getClientEntityId(serverEntityId);
    if (clientEntityId === undefined) return null;

    const displayName = this.clientWorld.getWorld().getComponent(clientEntityId, 'DisplayName');
    return displayName?.name ?? null;
  }

  handleContent(payload: DialogueContentPayload): void {
    // Block movement input when dialogue becomes active
    if (!this.state.active) {
      this.inputBlocking?.block('dialogue');
    }
    this.state.active = true;
    this.state.text = payload.text;
    // Use speaker from ink tag, or fall back to entity's DisplayName from the world
    this.state.speaker =
      payload.speaker ?? this.resolveDisplayName(payload.targetEntityId) ?? undefined;
    this.state.choices = payload.choices;
    this.state.canContinue = payload.canContinue;
    this.state.targetEntityId = payload.targetEntityId;

    // Resolve spritesheet ID for portrait rendering
    this.state.spritesheetId = this.resolveSpritesheetId(payload.targetEntityId);
  }

  /**
   * Handle dialogue ended from the server.
   */
  handleEnded(): void {
    this.inputBlocking?.unblock('dialogue');
    this.state.active = false;
    this.state.text = '';
    this.state.speaker = undefined;
    this.state.choices = [];
    this.state.canContinue = false;
    this.state.targetEntityId = null;
    this.state.spritesheetId = null;
  }

  /**
   * Player selected a choice.
   */
  selectChoice(choiceIndex: number): void {
    if (this.sendChoice) {
      this.sendChoice(choiceIndex);
    }
  }

  /**
   * Player clicked to continue.
   */
  continue(): void {
    if (this.sendContinue) {
      this.sendContinue();
    }
  }

  /**
   * Player requested to exit the dialogue.
   */
  requestExit(): void {
    if (this.sendExit) {
      this.sendExit();
    }
  }

  /**
   * Check if dialogue is blocking input.
   */
  isActive(): boolean {
    return this.state.active;
  }
}
